import type { LambAnalytics } from '../core/sdk';
import type { EcommerceItem } from './product';

const PURCHASE_DEDUP_KEY = '_la_purchase_dedup';
const DEDUP_TTL_MS = 5 * 60 * 1000; // 5분

export interface CheckoutParams {
  currency?: string;
  value?: number;
  coupon?: string;
  items: EcommerceItem[];
}

export interface ShippingInfoParams {
  currency?: string;
  value?: number;
  shipping_tier?: string;
  items: EcommerceItem[];
}

export interface PaymentInfoParams {
  currency?: string;
  value?: number;
  payment_type?: string;
  items: EcommerceItem[];
}

export interface PurchaseParams {
  transaction_id: string;
  currency?: string;
  value?: number;
  shipping?: number;
  tax?: number;
  coupon?: string;
  items: EcommerceItem[];
}

export interface RefundParams {
  transaction_id: string;
  currency?: string;
  value?: number;
  items?: EcommerceItem[];
}

export class PurchaseTracker {
  private sdk: LambAnalytics;

  constructor(sdk: LambAnalytics) {
    this.sdk = sdk;
  }

  beginCheckout(params: CheckoutParams): void {
    this.sdk.track('begin_checkout', {
      currency: params.currency,
      value: params.value,
      coupon: params.coupon,
      items: params.items,
    });
  }

  addShippingInfo(params: ShippingInfoParams): void {
    this.sdk.track('add_shipping_info', {
      currency: params.currency,
      value: params.value,
      shipping_tier: params.shipping_tier,
      items: params.items,
    });
  }

  addPaymentInfo(params: PaymentInfoParams): void {
    this.sdk.track('add_payment_info', {
      currency: params.currency,
      value: params.value,
      payment_type: params.payment_type,
      items: params.items,
    });
  }

  purchase(params: PurchaseParams): void {
    if (!params.transaction_id) {
      if (typeof console !== 'undefined') {
        console.warn('[LambAnalytics] purchase event requires transaction_id');
      }
      return;
    }

    if (this.isDuplicatePurchase(params.transaction_id)) {
      return;
    }

    this.markPurchaseSent(params.transaction_id);

    this.sdk.track('purchase', {
      transaction_id: params.transaction_id,
      currency: params.currency,
      value: params.value,
      shipping: params.shipping,
      tax: params.tax,
      coupon: params.coupon,
      items: params.items,
    });
  }

  refund(params: RefundParams): void {
    if (!params.transaction_id) {
      if (typeof console !== 'undefined') {
        console.warn('[LambAnalytics] refund event requires transaction_id');
      }
      return;
    }

    this.sdk.track('refund', {
      transaction_id: params.transaction_id,
      currency: params.currency,
      value: params.value,
      items: params.items,
    });
  }

  /** 동일 transaction_id 5분 내 재전송 차단 */
  private isDuplicatePurchase(transactionId: string): boolean {
    if (typeof sessionStorage === 'undefined') return false;

    try {
      const raw = sessionStorage.getItem(PURCHASE_DEDUP_KEY);
      if (!raw) return false;

      const map = JSON.parse(raw) as Record<string, number>;
      const timestamp = map[transactionId];
      if (!timestamp) return false;

      return (Date.now() - timestamp) < DEDUP_TTL_MS;
    } catch {
      return false;
    }
  }

  private markPurchaseSent(transactionId: string): void {
    if (typeof sessionStorage === 'undefined') return;

    try {
      const raw = sessionStorage.getItem(PURCHASE_DEDUP_KEY);
      const map: Record<string, number> = raw ? JSON.parse(raw) as Record<string, number> : {};
      const now = Date.now();

      for (const key of Object.keys(map)) {
        if ((now - map[key]) >= DEDUP_TTL_MS) {
          delete map[key];
        }
      }

      map[transactionId] = now;
      sessionStorage.setItem(PURCHASE_DEDUP_KEY, JSON.stringify(map));
    } catch {
      // no-op: sessionStorage may be unavailable
    }
  }
}
